import React, { useEffect, useMemo, useState } from "react";
import {
  type AgentAvatarColour,
  type AgentAvatarCut,
  isAgentAvatarColour,
  isAgentAvatarCut,
  resolveAgentAvatarDisplay,
} from "./agentAvatarIdentity";
import {
  getAgentActivityExpiry,
  resolveAgentActivityDisplay,
  type AgentActivityDisplay,
} from "../utils/agentActivityDisplay";

type Vec = [number, number, number];
type Face = number[];
type Mesh = { vertices: Vec[]; faces: Face[]; sortFaces: boolean };

const TILT = (24 * Math.PI) / 180;
const LIGHT: Vec = [-0.42, 0.8, 0.44];
const LIGHT_LENGTH = Math.hypot(...LIGHT);
const UNIT_LIGHT: Vec = [
  LIGHT[0] / LIGHT_LENGTH,
  LIGHT[1] / LIGHT_LENGTH,
  LIGHT[2] / LIGHT_LENGTH,
];
const PALETTE: Record<AgentAvatarColour, [number, number, number, number]> = {
  azure: [212, 64, 18, 48],
  blue: [240, 56, 18, 48],
  violet: [268, 52, 18, 48],
  orchid: [296, 46, 18, 48],
  rose: [324, 50, 18, 48],
  crimson: [352, 58, 18, 48],
  tangerine: [20, 68, 18, 48],
  amber: [46, 70, 18, 48],
  olive: [74, 56, 18, 48],
  moss: [102, 50, 18, 48],
  green: [130, 46, 18, 48],
  teal: [158, 52, 18, 48],
  cyan: [186, 56, 18, 48],
  white: [0, 0, 64, 30],
  grey: [210, 6, 33, 33],
  black: [220, 8, 7, 25],
};

/**
 * Keep an attributed agent reply visibly tied to the same resolved palette as
 * its gemstone while preserving readable foreground contrast in either theme.
 */
export function getAgentGemstoneBubbleTone(
  _id: number,
  identity: { cut?: unknown; colour?: unknown },
) {
  const display = resolveAgentAvatarDisplay(identity);
  if (display.colour === null) {
    return { colour: null, backgroundColor: "#f1f3f5", color: "#374151" };
  }
  const [hue, saturation] = PALETTE[display.colour];
  return {
    colour: display.colour,
    backgroundColor: `hsl(${hue} ${Math.min(saturation, 54)}% 94%)`,
    color: `hsl(${hue} ${Math.max(32, saturation)}% 20%)`,
  };
}

const ring = (
  vertices: Vec[],
  count: number,
  radius: number,
  y: number,
  squash = 1,
  offset = 0,
) =>
  Array.from({ length: count }, (_, index) => {
    const angle = offset + (index * Math.PI * 2) / count;
    vertices.push([
      Math.cos(angle) * radius,
      y,
      Math.sin(angle) * radius * squash,
    ]);
    return vertices.length - 1;
  });
// The ring advances counter-clockwise in x/z. These helpers use explicit
// outward winding: no global-centroid repair (which is invalid for a torus's
// inner wall) is needed or applied.
const band = (faces: Face[], lower: number[], upper: number[]) =>
  lower.forEach((point, index) =>
    faces.push([
      point,
      upper[index],
      upper[(index + 1) % upper.length],
      lower[(index + 1) % lower.length],
    ]),
  );
const topFan = (faces: Face[], points: number[], apex: number) =>
  points.forEach((point, index) =>
    faces.push([point, apex, points[(index + 1) % points.length]]),
  );
const bottomFan = (faces: Face[], points: number[], apex: number) =>
  points.forEach((point, index) =>
    faces.push([point, points[(index + 1) % points.length], apex]),
  );
const topCap = (faces: Face[], points: number[]) =>
  faces.push([...points].reverse());
const bottomCap = (faces: Face[], points: number[]) => faces.push(points);

function meshFor(cut: AgentAvatarCut): Mesh {
  const vertices: Vec[] = [];
  const faces: Face[] = [];
  const complete = (sortFaces = false): Mesh => ({
    vertices,
    faces,
    sortFaces,
  });
  const apex = (x: number, y: number, z: number) => {
    vertices.push([x, y, z]);
    return vertices.length - 1;
  };
  const prism = (
    count: number,
    radius: number,
    height: number,
    taper = 1,
    squash = 1,
  ) => {
    const lower = ring(vertices, count, radius, -height / 2, squash);
    const upper = ring(vertices, count, radius * taper, height / 2, squash);
    band(faces, lower, upper);
    bottomCap(faces, lower);
    topCap(faces, upper);
  };
  switch (cut) {
    case "brilliant": {
      const base = ring(vertices, 8, 1, 0);
      const top = ring(vertices, 8, 0.43, 0.28);
      band(faces, base, top);
      topCap(faces, top);
      bottomFan(faces, base, apex(0, -0.95, 0));
      break;
    }
    case "rhomboid":
      prism(4, 1, 1.15, 1, 0.75);
      vertices.slice(4).forEach((point) => {
        point[0] += 0.35;
      });
      break;
    // The u/v quad follows the tube perimeter before advancing around the
    // ring. This preserves outward winding without crossing its diagonals.
    // Sorting is still required to retain the open centre in SVG.
    case "torus": {
      const major = 1,
        minor = 0.31,
        rows = Array.from({ length: 12 }, (_, i) =>
          Array.from({ length: 7 }, (_, j) => {
            const u = (i * Math.PI * 2) / 12,
              v = (j * Math.PI * 2) / 7,
              radius = major + minor * Math.cos(v);
            vertices.push([
              radius * Math.cos(u),
              minor * Math.sin(v),
              radius * Math.sin(u),
            ]);
            return vertices.length - 1;
          }),
        );
      rows.forEach((row, i) =>
        row.forEach((point, j) =>
          faces.push([
            point,
            row[(j + 1) % 7],
            rows[(i + 1) % 12][(j + 1) % 7],
            rows[(i + 1) % 12][j],
          ]),
        ),
      );
      return complete(true);
    }
    case "pyramid": {
      const base = ring(vertices, 4, 1, -0.65, 1, Math.PI / 4);
      bottomCap(faces, base);
      topFan(faces, base, apex(0, 0.85, 0));
      break;
    }
    case "ellipsoid":
    case "sphere": {
      const squash = cut === "ellipsoid" ? 0.75 : 1,
        height = cut === "ellipsoid" ? 1.18 : 1;
      const rows = [-0.72, -0.25, 0.25, 0.72].map((y) =>
        ring(vertices, 12, Math.sqrt(1 - y * y), y * height, squash),
      );
      for (let i = 0; i < rows.length - 1; i += 1)
        band(faces, rows[i], rows[i + 1]);
      bottomFan(faces, rows[0], apex(0, -height, 0));
      topFan(faces, rows[3], apex(0, height, 0));
      break;
    }
    case "step": {
      const base = ring(vertices, 8, 1.12, 0);
      const middle = ring(vertices, 8, 0.82, 0.18);
      const top = ring(vertices, 8, 0.55, 0.36);
      const bottom = ring(vertices, 8, 0.58, -0.52);
      band(faces, base, middle);
      band(faces, middle, top);
      band(faces, bottom, base);
      topCap(faces, top);
      bottomCap(faces, bottom);
      break;
    }
    case "wedge": {
      const a = apex(-1, -0.55, 0.72),
        b = apex(1, -0.55, 0.72),
        c = apex(0, 0.82, 0.72),
        d = apex(-1, -0.55, -0.72),
        e = apex(1, -0.55, -0.72),
        f = apex(0, 0.82, -0.72);
      faces.push(
        [a, b, c],
        [d, f, e],
        [a, d, e, b],
        [b, e, f, c],
        [c, f, d, a],
      );
      break;
    }
    case "cabochon": {
      const base = ring(vertices, 10, 1, 0),
        middle = ring(vertices, 10, 0.85, 0.38),
        top = ring(vertices, 10, 0.35, 0.66);
      band(faces, base, middle);
      band(faces, middle, top);
      bottomCap(faces, base);
      topFan(faces, top, apex(0, 0.76, 0));
      break;
    }
    case "spire": {
      const base = ring(vertices, 5, 0.75, -0.88);
      bottomCap(faces, base);
      topFan(faces, base, apex(0, 1.05, 0));
      break;
    }
    case "tablet":
      prism(8, 1.12, 0.35, 0.78, 0.82);
      break;
    case "bipyramid": {
      const base = ring(vertices, 6, 1, 0);
      topFan(faces, base, apex(0, 1, 0));
      bottomFan(faces, base, apex(0, -1, 0));
      break;
    }
    case "barrel": {
      const base = ring(vertices, 10, 0.8, -0.38),
        middle = ring(vertices, 10, 1, 0),
        top = ring(vertices, 10, 0.8, 0.38);
      band(faces, base, middle);
      band(faces, middle, top);
      bottomCap(faces, base);
      topCap(faces, top);
      break;
    }
    case "star": {
      const base = ring(vertices, 10, 1, 0).map((index, i) => {
        if (i % 2) {
          vertices[index][0] *= 0.45;
          vertices[index][2] *= 0.45;
        }
        return index;
      });
      topFan(faces, base, apex(0, 0.62, 0));
      bottomFan(faces, base, apex(0, -0.62, 0));
      return complete(true);
    }
    case "cone": {
      const base = ring(vertices, 18, 1, -0.68);
      bottomCap(faces, base);
      topFan(faces, base, apex(0, 0.88, 0));
      break;
    }
    case "column":
      prism(6, 0.82, 1.12, 0.9);
      break;
  }
  return complete();
}

const faceNormal = (vertices: Vec[], indices: Face): Vec => {
  const a = vertices[indices[0]],
    b = vertices[indices[1]],
    c = vertices[indices[2]];
  const ux = b[0] - a[0],
    uy = b[1] - a[1],
    uz = b[2] - a[2];
  const vx = c[0] - a[0],
    vy = c[1] - a[1],
    vz = c[2] - a[2];
  const x = uy * vz - uz * vy,
    y = uz * vx - ux * vz,
    z = ux * vy - uy * vx;
  const length = Math.hypot(x, y, z) || 1;
  return [x / length, y / length, z / length];
};
const project = (vertices: Vec[], rotation: number): Vec[] =>
  vertices.map(([x, y, z]) => {
    const rotatedX = x * Math.cos(rotation) + z * Math.sin(rotation);
    const rotatedZ = -x * Math.sin(rotation) + z * Math.cos(rotation);
    return [
      rotatedX,
      y * Math.cos(TILT) - rotatedZ * Math.sin(TILT),
      y * Math.sin(TILT) + rotatedZ * Math.cos(TILT),
    ];
  });
const faceDepth = (vertices: Vec[], indices: Face) =>
  indices.reduce((sum, index) => sum + vertices[index][2], 0) / indices.length;

export function getGemstoneMeshDiagnostics(
  cut: AgentAvatarCut,
  rotation: number,
) {
  const mesh = meshFor(cut);
  const projected = project(mesh.vertices, rotation);
  const visibleFaces = mesh.faces
    .map((indices, index) => ({
      index,
      indices,
      normal: faceNormal(projected, indices),
    }))
    .filter((entry) => entry.normal[2] > 0);
  if (mesh.sortFaces)
    visibleFaces.sort(
      (a, b) =>
        faceDepth(projected, a.indices) - faceDepth(projected, b.indices),
    );
  return {
    totalFaceCount: mesh.faces.length,
    sortFaces: mesh.sortFaces,
    visibleFaceIndexes: visibleFaces.map((entry) => entry.index),
    visibleFaceDepths: visibleFaces.map((entry) =>
      faceDepth(projected, entry.indices),
    ),
    projectedVertices: projected,
  };
}

/** Immutable topology for focused winding/cap tests. */
export function getGemstoneMeshTopology(cut: AgentAvatarCut) {
  const mesh = meshFor(cut);
  return {
    vertices: mesh.vertices.map((vertex) => [...vertex] as Vec),
    faces: mesh.faces.map((face) => [...face]),
  };
}

export interface AgentGemstoneProps {
  id: number;
  cut?: unknown;
  colour?: unknown;
  state?: "idle" | "working" | "error";
  agentStatus?: string | null;
  agentLastHeartbeatAt?: string | null;
  activity?: AgentActivityDisplay;
  size?: number;
  title?: string;
  testId?: string;
  showStatusCircle?: boolean;
}

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;

/** Matte 3D avatar renderer shared by agent inventory, picker and attributed chat. */
export function AgentGemstone({
  id,
  cut,
  colour,
  state = "idle",
  agentStatus,
  agentLastHeartbeatAt,
  activity: controlledActivity,
  size = 38,
  title,
  testId,
  showStatusCircle = false,
}: AgentGemstoneProps) {
  const display = resolveAgentAvatarDisplay({ cut, colour });
  const [, setClock] = useState(0);
  const activity: AgentActivityDisplay =
    controlledActivity ??
    (agentStatus === undefined
      ? state === "error"
        ? "idle"
        : state
      : resolveAgentActivityDisplay(agentStatus, agentLastHeartbeatAt));
  useEffect(() => {
    if (controlledActivity !== undefined) return undefined;
    const expiry = getAgentActivityExpiry(agentStatus, agentLastHeartbeatAt);
    if (expiry === null || expiry <= Date.now()) return undefined;
    const timer = window.setTimeout(
      () => setClock((value) => value + 1),
      expiry - Date.now(),
    );
    return () => window.clearTimeout(timer);
  }, [agentLastHeartbeatAt, agentStatus, controlledActivity]);
  const [rotation, setRotation] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  useEffect(() => {
    const query = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!query) return undefined;
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);
  const mesh = useMemo(
    () => (display.cut ? meshFor(display.cut) : null),
    [display.cut],
  );
  useEffect(() => {
    if (reducedMotion || state === "error") return undefined;
    let frame = 0;
    let previous = performance.now();
    const speed =
      activity === "working" ? 2.4 : activity === "finished" ? 0.75 : 0.5;
    const tick = (now: number) => {
      setRotation(
        (value) => value + Math.min(0.05, (now - previous) / 1000) * speed,
      );
      previous = now;
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [activity, reducedMotion, state]);
  const faces = useMemo(() => {
    if (!mesh || display.colour === null) return [];
    const projected = project(mesh.vertices, rotation);
    const visible = mesh.faces
      .map((indices) => ({ indices, normal: faceNormal(projected, indices) }))
      .filter((entry) => entry.normal[2] > 0);
    if (mesh.sortFaces)
      visible.sort(
        (a, b) =>
          faceDepth(projected, a.indices) - faceDepth(projected, b.indices),
      );
    const [hue, saturation, base, range] = PALETTE[display.colour];
    return visible.map(({ indices, normal }) => ({
      points: indices
        .map(
          (index) =>
            `${(43 + projected[index][0] * 28).toFixed(2)},${(43 - projected[index][1] * 28).toFixed(2)}`,
        )
        .join(" "),
      fill: `hsl(${hue} ${saturation}% ${base + range * (0.4 + 0.6 * Math.max(0, normal[0] * UNIT_LIGHT[0] + normal[1] * UNIT_LIGHT[1] + normal[2] * UNIT_LIGHT[2]))}%)`,
    }));
  }, [display.colour, mesh, rotation]);
  const label =
    display.status === "unassigned"
      ? title
        ? `${title} (unassigned)`
        : "Agent appearance unassigned"
      : title || `${display.colour} ${display.cut} gemstone`;
  const circleColour =
    activity === "working"
      ? "#2e7d32"
      : activity === "finished"
        ? "#d6a600"
        : null;
  const showCircle = showStatusCircle && circleColour !== null;
  const workingScale =
    showStatusCircle && activity === "working" && !reducedMotion ? 1.055 : 1;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 86 86"
      role="img"
      aria-label={showStatusCircle ? `${label}; ${activity} status` : label}
      data-testid={testId}
      data-agent-id={id}
      data-gemstone-status={display.status}
      data-gemstone-cut={display.cut ?? undefined}
      data-gemstone-colour={display.colour ?? undefined}
      data-gemstone-activity={activity}
      data-gemstone-status-circle={showCircle ? activity : "none"}
      data-gemstone-motion={reducedMotion ? "reduced" : "allowed"}
      data-gemstone-rotation={rotation.toFixed(4)}
      focusable="false"
      style={{
        overflow: "visible",
        transform: `scale(${workingScale})`,
        transformOrigin: "center",
        transition: reducedMotion ? "none" : "transform 180ms ease-out",
      }}
    >
      <title>{label}</title>
      {showCircle && (
        <circle
          data-testid={testId ? `${testId}-status-circle` : undefined}
          data-gemstone-layer="background"
          cx="43"
          cy="43"
          r="40"
          fill={circleColour}
          opacity="0.28"
        />
      )}
      {display.status === "unassigned" ? (
        <path
          data-testid={testId ? `${testId}-unassigned-placeholder` : undefined}
          data-gemstone-layer="unassigned-placeholder"
          d="M43 9 71 43 43 77 15 43Z"
          fill="#f1f3f5"
          stroke="#6b7280"
          strokeWidth="4"
          strokeLinejoin="round"
        />
      ) : (
        <g data-gemstone-layer="facets">
          {faces.map((face, index) => (
            <polygon
              key={`${display.cut}-${index}`}
              data-testid={testId ? `${testId}-face-${index}` : undefined}
              points={face.points}
              fill={face.fill}
              shapeRendering="geometricPrecision"
            />
          ))}
        </g>
      )}
    </svg>
  );
}

export const isGemstoneCut = isAgentAvatarCut;
export const isGemstoneColour = isAgentAvatarColour;
